/**
 * ========================================
 * 📦 PURCHASE ORDER RECEIVING API HOOKS
 * ========================================
 *
 * Custom React Query hooks for receiving purchase orders.
 * Implements idempotent receiving with atomic operations.
 *
 * ========================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { httpClient } from '../http-client.js'

// ========================================
// TYPE DEFINITIONS
// ========================================

/**
 * Per-unit dimensions captured at receive time for slab line items.
 * One entry per inventory item that will be created — order matches the
 * order of the inventory items the server creates for this line item.
 *
 * Why: slabs are billed by square foot, so the actual L×W received may
 * differ from what was ordered (damage, mill variance). Thickness varies
 * too (2cm, 0.8", 13/16", etc.) and matters for programmers.
 */
export interface SerializedSlabReceive {
  length: number | null
  width: number | null
  thickness: string | null
  /** Block # entered at receive. F-5: one per line item — written back to POLI.block_numbers (the source) and mirrored to every slab. Null keeps the line's value. */
  block_number?: string | null
  /** Per-slab finish override (F5). Falls back to material's finish when null. */
  finish?: string | null
  /** Per-slab destination location (F7) — UUID into inventory_locations. */
  inventory_location_id?: string | null
}

export interface ReceiveLineItemRequest {
  lineItemId: string
  quantityToReceive: number
  numberOfItems?: number // Optional: create fewer inventory items than quantity (for non-consumables)
  notes?: string
  deliveryDate?: string // ISO date string
  /**
   * Per-slab actual dimensions captured at receive. Length must equal the
   * number of inventory items the server will create for this line item
   * (numberOfItems if provided, else quantityToReceive). Only meaningful
   * for slab department line items; ignored for tile/consumable.
   */
  serializedItems?: SerializedSlabReceive[]
}

export interface ReceivePurchaseOrderRequest {
  lineItems: ReceiveLineItemRequest[]
  globalNotes?: string
}

export interface LineItemResult {
  lineItemId: string
  quantityReceived: number
  newStatus: string
  newReceivedQuantity: number
  inventoryItemsCreated: string[] // MSN serial numbers
}

export interface ReceivingResult {
  success: true
  receiptId: string
  itemsCreated: number
  lineItemResults: LineItemResult[]
}

export interface ReceivingError {
  error: string
  message: string
  details?: Record<string, any>
}

// ========================================
// API FUNCTIONS
// ========================================

/**
 * Generate a UUID v4 for idempotency key
 */
function generateIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * Receive items for a purchase order
 *
 * Note: this endpoint is at `/api/receive-purchase-order/:id` (no `/v1`
 * prefix), so we use `fetch` directly instead of the shared `httpClient`,
 * which would prepend `/api/v1` and produce a 404. The offline replay
 * handler in `client/field/offline-queue/registerHandlers.ts` and the
 * warehouse `ReceivePage.tsx` follow the same convention.
 */
async function receivePurchaseOrder(
  purchaseOrderId: string,
  request: ReceivePurchaseOrderRequest,
  idempotencyKey?: string
): Promise<ReceivingResult> {
  const key = idempotencyKey || generateIdempotencyKey()

  const response = await fetch(`/api/receive-purchase-order/${purchaseOrderId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': key
    },
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    let errorBody: ReceivingError | undefined
    try {
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        errorBody = await response.json()
      }
    } catch {
      // ignore — fall through to plain HTTP error
    }

    const message =
      errorBody?.message ||
      errorBody?.error ||
      response.statusText ||
      `HTTP ${response.status}`
    const error = new Error(`HTTP ${response.status}: ${message}`) as Error & {
      status?: number
      response?: { data?: ReceivingError }
    }
    error.status = response.status
    if (errorBody) {
      error.response = { data: errorBody }
    }
    throw error
  }

  return response.json() as Promise<ReceivingResult>
}

// ========================================
// REACT QUERY HOOKS
// ========================================

/**
 * Hook for receiving purchase order items
 *
 * Features:
 * - Automatic idempotency key generation
 * - Safe retries (returns cached result for duplicate requests)
 * - Automatic cache invalidation on success
 *
 * @example
 * const receiveMutation = useReceivePurchaseOrder()
 *
 * await receiveMutation.mutateAsync({
 *   purchaseOrderId: 'po-id',
 *   request: {
 *     lineItems: [{
 *       lineItemId: 'item-1',
 *       quantityToReceive: 5,
 *       notes: 'First shipment'
 *     }],
 *     globalNotes: 'Delivery on time'
 *   }
 * })
 */
export function useReceivePurchaseOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      purchaseOrderId,
      request,
      idempotencyKey
    }: {
      purchaseOrderId: string
      request: ReceivePurchaseOrderRequest
      idempotencyKey?: string
    }) => receivePurchaseOrder(purchaseOrderId, request, idempotencyKey),

    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['purchase_orders', variables.purchaseOrderId] })
      queryClient.invalidateQueries({ queryKey: ['purchase_order_line_items'] })
      queryClient.invalidateQueries({ queryKey: ['inventory_items'] })
      queryClient.invalidateQueries({ queryKey: ['receiving_transactions'] })
      // Pre-print + View Serials dialogs: pre-printed items just flipped out of In Transit
      queryClient.invalidateQueries({ queryKey: ['purchase-order-serialized-items', variables.purchaseOrderId] })

      console.log(`✅ Successfully received ${data.itemsCreated} items (Receipt: ${data.receiptId})`)
    },

    onError: (error: any) => {
      console.error('❌ Error receiving purchase order:', error)
    }
  })
}

/**
 * Append a note to ONE PO line's notes (card 30nNybLD8IRK). Used by the
 * partial-receipt note prompt on the web and on /field/receive. The server
 * appends on a new line (never overwrites), trims and caps the text, writes
 * only the notes column, and allows anyone who may receive (warehouse
 * logins included), unlike the generated line PUT (purchase_orders:update).
 */
export async function appendPurchaseOrderLineNote(
  lineItemId: string,
  text: string
): Promise<{ id: string; notes: string }> {
  const res = await httpClient.post<{
    success: boolean
    data?: { id: string; notes: string }
    error?: string
  }>(`/workflows/purchase-order-receiving/line-items/${lineItemId}/append-note`, { text })
  if (!res?.success || !res.data) {
    throw new Error(res?.error || 'The note could not be saved.')
  }
  return res.data
}

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Format date for API - returns YYYY-MM-DD format in local timezone
 */
export function formatDateForApi(date: Date | undefined): string | undefined {
  if (!date) {return undefined}

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Calculate remaining quantity for a line item
 */
export function calculateRemaining(ordered: number, received: number): number {
  return Math.max(0, ordered - received)
}

const AREA_UNITS = new Set(['sqft', 'sf', 'ft2'])

/**
 * A tile line ordered by area (sqft) may be received over what's outstanding:
 * whole boxes usually come out a few sqft over the ordered amount, and the
 * warehouse enters what actually arrived. Slabs and count-unit tile lines
 * (ea, pc, …) keep the "no more than remaining" rule. Same rule as the
 * server's allowsOverReceipt (ReceivePurchaseOrderService).
 */
export function allowsOverReceipt(
  department: string | null | undefined,
  unitOfMeasure: string | null | undefined
): boolean {
  return department === 'tile' && AREA_UNITS.has((unitOfMeasure ?? '').trim().toLowerCase())
}

/**
 * Validate receive quantity for ONE line.
 *
 * 0 (or a blank input, which the receive UIs read as 0) is valid: it means
 * "not receiving this line now", so a multi-line PO can be received one line
 * at a time. Whether the receipt as a whole has anything in it is
 * canSubmitReceive's job. `allowOver` (see allowsOverReceipt) lifts the
 * "no more than remaining" limit.
 */
export function validateReceiveQuantity(
  quantityToReceive: number,
  remaining: number,
  allowOver = false
): { valid: boolean; error?: string } {
  if (!Number.isFinite(quantityToReceive)) {
    return { valid: false, error: 'Quantity must be a whole number' }
  }

  if (quantityToReceive < 0) {
    return { valid: false, error: 'Quantity cannot be negative' }
  }

  if (!Number.isInteger(quantityToReceive)) {
    return { valid: false, error: 'Quantity must be a whole number' }
  }

  if (quantityToReceive > remaining && !allowOver) {
    return {
      valid: false,
      error: `Cannot receive ${quantityToReceive} items. Only ${remaining} remaining.`
    }
  }

  return { valid: true }
}

/**
 * A receipt can be submitted when at least one line has a quantity above 0
 * and every line's quantity is valid (lines at 0 are simply not received).
 */
export function canSubmitReceive(
  lines: Array<{ quantityToReceive: number; remaining: number }>
): boolean {
  return (
    lines.some((line) => line.quantityToReceive > 0) &&
    lines.every((line) => validateReceiveQuantity(line.quantityToReceive, line.remaining).valid)
  )
}

/**
 * Why the receive submit button is disabled (its tooltip), or undefined when
 * it can be clicked. Same rules as canSubmitReceive; an over-limit line names
 * the problem instead of asking for a quantity.
 */
export function receiveSubmitBlockedReason(
  lines: Array<{ quantityToReceive: number; remaining: number }>,
  preflightBlocked: boolean
): string | undefined {
  if (preflightBlocked) {return 'Fix the preflight issues above before submitting.'}
  const invalid = lines
    .map((line) => validateReceiveQuantity(line.quantityToReceive, line.remaining))
    .find((validation) => !validation.valid)
  if (invalid) {return `Fix the quantity first: ${invalid.error}`}
  if (!lines.some((line) => line.quantityToReceive > 0)) {return 'Enter a quantity on at least one line.'}
  return undefined
}

/**
 * One receipt line split into the pre-printed serials it uses up and the new
 * serials it creates. The server reuses the line's In Transit (pre-printed)
 * items first, then creates numberOfItems - reused new ones, so the request
 * sends numberOfItems = reused + newSerials.
 */
export interface ReceiveSerialSplit {
  /** Pre-printed (In Transit) items this receipt uses: min(inTransit, quantity). */
  reused: number
  /** New serials to create on top of the reused ones. */
  newSerials: number
  /** What the request sends: reused + newSerials. */
  numberOfItems: number
}

/**
 * Split a receipt line into reused pre-printed items and new serials.
 * `newSerials` is what the user asked for, clamped to 0..quantity - reused so
 * numberOfItems never exceeds the quantity (the server rejects that). It may
 * be 0 even with no pre-printed items: the quantity is still recorded as
 * received, just with no serials made. Everything is 0 when the line isn't
 * being received.
 */
export function splitReceiveSerials(
  quantityToReceive: number,
  inTransitCount: number,
  newSerials: number
): ReceiveSerialSplit {
  if (!(quantityToReceive > 0)) {return { reused: 0, newSerials: 0, numberOfItems: 0 }}
  const reused = Math.min(Math.max(0, inTransitCount || 0), quantityToReceive)
  const most = quantityToReceive - reused
  const clamped = Math.min(Math.max(0, newSerials || 0), most)
  return { reused, newSerials: clamped, numberOfItems: reused + clamped }
}

/**
 * "Serials to Create" for a non-consumable line. The server creates one
 * inventory item per unit (tile and slab alike, current_quantity 1), so the
 * count follows the quantity being received — unless the user typed a count
 * by hand, which is kept but clamped to reused..quantity (0 is allowed; see
 * splitReceiveSerials). Never above the
 * quantity (the server rejects numberOfItems > quantityToReceive); 0 when the
 * line isn't being received.
 */
export function serialCountForQuantity(
  quantityToReceive: number,
  currentCount: number,
  editedByHand: boolean,
  inTransitCount = 0
): number {
  if (!(quantityToReceive > 0)) {return 0}
  if (!editedByHand) {return quantityToReceive}
  const { reused } = splitReceiveSerials(quantityToReceive, inTransitCount, 0)
  return splitReceiveSerials(quantityToReceive, inTransitCount, currentCount - reused).numberOfItems
}

/**
 * The receive dialog's form fields the user types into. A line the user has
 * touched keeps these when its line items / materials are fetched again.
 */
export const RECEIVE_LINE_USER_INPUT_FIELDS = [
  'edited',
  'quantityToReceive',
  'numberOfItems',
  'serialsEdited',
  'notes',
  'deliveryDate',
  'blockNumber',
  'slabRows',
  'batchRow',
] as const

type ReceiveLineUserInput = Record<(typeof RECEIVE_LINE_USER_INPUT_FIELDS)[number], unknown>

/**
 * Merge freshly built receive lines (defaults: Quantity to Receive = Remaining)
 * into the lines on screen. A line the user has not edited takes the fresh
 * defaults; an edited line keeps everything the user typed and only takes the
 * fresh read-only facts (ordered / received / remaining, material info). Lines
 * no longer in `fetched` (fully received) drop out; new ones get defaults.
 */
export function mergeFetchedReceiveLines<
  T extends { lineItemId: string; edited: boolean } & ReceiveLineUserInput,
>(current: T[], fetched: T[]): T[] {
  const onScreen = new Map(current.map((line) => [line.lineItemId, line]))
  return fetched.map((line) => {
    const mine = onScreen.get(line.lineItemId)
    if (!mine?.edited) {return line}
    const merged: T = { ...line }
    for (const field of RECEIVE_LINE_USER_INPUT_FIELDS) {
      (merged as ReceiveLineUserInput)[field] = mine[field]
    }
    return merged
  })
}

/**
 * Check if line item is fully received
 */
export function isFullyReceived(ordered: number, received: number): boolean {
  return received >= ordered
}

/**
 * Check if line item is partially received
 */
export function isPartiallyReceived(ordered: number, received: number): boolean {
  return received > 0 && received < ordered
}
